import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Trash2 } from 'lucide-react';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'primary';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  confirmLoading?: boolean;
  /** Prevent dismiss while an async confirm is running */
  closeDisabled?: boolean;
  variant?: ConfirmDialogVariant;
  /** Override default variant icon */
  icon?: React.ReactNode;
  maxWidth?: 'sm' | 'md';
}

const variantStyles: Record<
  ConfirmDialogVariant,
  { iconWrap: string; icon: React.ReactNode; confirmBtn: string }
> = {
  danger: {
    iconWrap: 'bg-red-100 dark:bg-red-900/30',
    icon: <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />,
    confirmBtn: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    iconWrap: 'bg-amber-100 dark:bg-amber-900/30',
    icon: <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />,
    confirmBtn: 'bg-amber-600 hover:bg-amber-700',
  },
  primary: {
    iconWrap: 'bg-primary-100 dark:bg-primary-900/30',
    icon: <AlertTriangle className="h-5 w-5 text-primary-600 dark:text-primary-400" />,
    confirmBtn: 'bg-primary-600 hover:bg-primary-700',
  },
};

/**
 * In-app confirmation dialog (replaces window.confirm).
 * Matches ApplicationDetail delete modal: icon badge, title, message, cancel + confirm.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  confirmLoading = false,
  closeDisabled = false,
  variant = 'danger',
  icon,
  maxWidth = 'md',
}) => {
  const { t } = useTranslation();
  if (!open) return null;

  const styles = variantStyles[variant];
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const blocked = closeDisabled || confirmLoading;
  const maxWidthClass = maxWidth === 'sm' ? 'max-w-sm' : 'max-w-md';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto"
      onClick={() => !blocked && onClose()}
      role="presentation"
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full ${maxWidthClass} flex flex-col`}
        onClick={e => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-start gap-3">
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${styles.iconWrap}`}
          >
            {icon ?? styles.icon}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              id="confirm-dialog-title"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              {title}
            </h3>
            <div
              id="confirm-dialog-message"
              className="text-sm text-gray-500 dark:text-gray-400 mt-1"
            >
              {typeof message === 'string' ? <p>{message}</p> : message}
            </div>
          </div>
        </div>
        <div className="p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={blocked}
            className="px-4 py-2 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            {resolvedCancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={blocked}
            className={`px-4 py-2 rounded-lg font-medium text-white disabled:opacity-50 disabled:pointer-events-none inline-flex items-center gap-1.5 ${styles.confirmBtn}`}
          >
            {confirmLoading ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {confirmLabel}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
